import { Tooltip } from "@mui/material";

import { SelectField } from "../../components/v3";
import { ProjectService } from "../../services";

interface AmountToPayColumnsOptions {
  /** Per-project cache of `ProjectStage` join rows (keyed by projectId), populated by
   * `ensureProjectStages` — used to scope the Stage dropdown's options to the row's project. */
  projectStagesCache?: Record<number, any[]>;
  /** Fetches (and caches) the `ProjectStage` rows for a project; called when a row's project changes. */
  ensureProjectStages?: (projectId: number) => Promise<any[]>;
  /** Gates the Welfare column's visibility (VIEW_WORKER_WELFARE) — hidden by default. The
   * underlying `row.welfare` value still flows into the amount-to-pay/deduction math regardless;
   * this only controls whether the column itself is shown. */
  canViewWelfare?: boolean;
}

/** Grid columns for the weekly worker payment table. */
export const getAmountToPayColumns = ({
  // Unused while the Stage column (and its stage-loading) is disabled (see below) — kept in the
  // signature so re-enabling it is a one-line change for the caller.
  projectStagesCache: _projectStagesCache = {},
  ensureProjectStages: _ensureProjectStages = async () => [],
  canViewWelfare = false,
}: AmountToPayColumnsOptions = {}) =>
  [
    {
      field: "name",
      headerName: "Worker",
      description: "Worker this weekly payment entry is for",
      flex: 1.3,
      editable: false,
      renderCell: (params) =>
        params.row.disabled ? (
          <Tooltip title={params.row.disabledReason || ""}>
            <span>{params.value}</span>
          </Tooltip>
        ) : (
          params.value
        ),
    },
    {
      field: "balance",
      headerName: "Opening Balance",
      description: "Worker's running balance carried over from the previous weekly payment",
      flex: 0.8,
      editable: false,
      renderCell: (params) => {
        const val = Number(params.value || 0);
        const text = `₹ ${val.toLocaleString("en-IN")}`;
        if (!params.row.previousPaymentPending) return text;
        const period = params.row.previousPaymentPeriod;
        return (
          <Tooltip title={`Previous week's payment${period ? ` (${period})` : ""} is still pending`}>
            <span
              style={{
                backgroundColor: "#FFF3CD",
                color: "#8A6D3B",
                padding: "2px 8px",
                borderRadius: "4px",
                fontWeight: 600,
              }}
            >
              {text}
            </span>
          </Tooltip>
        );
      },
    },
    // Stage column hidden for now (per request) — the row still carries projectStageId/stageId/
    // stageName and submits whatever was already set, it's just not editable from this grid until
    // the Stage column is re-enabled.
    {
      field: "workHours",
      headerName: "Days",
      description: "No. of Days Worked",
      flex: 0.5,
      editable: true,
      type: "number",
      renderCell: (params) => {
        const val = Number(params.value || 0);
        return val > 0 ? `${val.toLocaleString("en-IN")} days` : "—";
      },
    },
    {
      field: "totalWorked",
      headerName: "Worked",
      description: "Total wage earned for the period",
      flex: 0.7,
      editable: false,
      renderCell: (params) => {
        const val = Number(params.value || 0);
        return val > 0 ? `₹ ${val.toLocaleString("en-IN")}` : "—";
      },
    },
    {
      field: "overtime",
      headerName: "OT",
      description: "Overtime amount for the period",
      flex: 0.6,
      editable: false,
      renderCell: (params) => {
        const val = Number(params.value || 0);
        return val > 0 ? `₹ ${val.toLocaleString("en-IN")}` : "—";
      },
    },
    {
      field: "advancePaid",
      headerName: "Advance Paid",
      description: "Advance payments already made to the worker within this pay period",
      flex: 0.8,
      editable: false,
      renderCell: (params) => {
        const val = Number(params.value || 0);
        return val > 0 ? `₹ ${val.toLocaleString("en-IN")}` : "—";
      },
    },
    {
      field: "welfare",
      headerName: "Welfare",
      description: "Welfare amount withheld this cycle — reduces Amount to Pay and is retained by the company",
      flex: 0.6,
      editable: true,
      renderCell: (params) => {
        const val = Number(params.value || 0);
        return val > 0 ? `₹ ${val.toLocaleString("en-IN")}` : "—";
      },
    },
    {
      field: "deduction",
      headerName: "Deduction",
      description:
        "Recovered against the worker's opening balance and this cycle's payout — reduces both the amount paid now " +
        "and the balance carried to next week. Can be negative to credit the worker back.",
      flex: 0.7,
      editable: true,
      type: "number",
      valueSetter: (value, row) => ({
        ...row,
        deduction: value === "" || value == null ? 0 : Number(value),
      }),
      // Unlike the other money columns, 0 can't be distinguished from "not set" here anyway (both
      // mean no adjustment), but a negative deduction is a real, meaningful value — show it.
      renderCell: (params) => {
        const val = Number(params.value || 0);
        return val !== 0 ? `₹ ${val.toLocaleString("en-IN")}` : "—";
      },
    },
    {
      field: "amountToPay",
      headerName: "Amount to Pay",
      description:
        "Net amount to pay the worker this cycle — this period's work plus overtime, minus welfare, advance paid and " +
        "deduction. Derived automatically, not directly editable.",
      flex: 0.8,
      editable: false,
      renderCell: (params) => {
        const val = Number(params.value || 0);
        return val > 0 ? `₹ ${val.toLocaleString("en-IN")}` : "—";
      },
    },
    {
      field: "closingBalance",
      headerName: "Closing Balance",
      description: "Opening Balance plus Deduction — carries forward as next cycle's Opening Balance",
      flex: 0.8,
      editable: false,
      renderCell: (params) => {
        const val = Number(params.value || 0);
        return `₹ ${val.toLocaleString("en-IN")}`;
      },
    },
    {
      field: "projectId",
      headerName: "Project *",
      description: "Project this worker's payment entry is linked to — required",
      flex: 1,
      editable: true,
      renderCell: (params) => params.row.projectName || "—",
      renderEditCell: (params) => (
        <SelectField
          size="small"
          value={params.row.projectId ? { id: params.row.projectId, name: params.row.projectName } : null}
          getData={ProjectService.getProjectList}
          getDataStaticFilters={{ status: ["INPROGRESS"], sortBy: "name", sortOrder: "asc" }}
          onChange={(project) => {
            params.api.updateRows([
              {
                id: params.id,
                projectId: project?.id,
                projectName: project?.name,
                // a new project invalidates the previously selected stage
                projectStageId: null,
                stageId: null,
                stageName: null,
              },
            ]);
            params.api.setEditCellValue({ id: params.id, field: "projectId", value: project?.id });
            // Stage loading disabled for now — the Stage column is hidden (no space for it in this
            // UI), so there's no need to warm its cache when the project changes.
            // if (project?.id) ensureProjectStages(project.id);
          }}
          style={{ width: "100%" }}
        />
      ),
    },
  ].filter((col) => canViewWelfare || col.field !== "welfare");

/**
 * Route paths for the transaction module, mounted under `/transaction`.
 * Single source of truth — reuse these instead of hard-coding `/transaction...` strings.
 * The child paths (other than `root`) are relative to the `/transaction` wrapper route.
 */
export const TRANSACTION_ROUTES = {
  root: "/transaction",
  details: ":id",
  add: "add",
  edit: "edit/:id",
} as const;

/**
 * Route paths for the weekly worker payment feature. Flat, absolute paths — unlike
 * TRANSACTION_ROUTES they are not nested under `/transaction`, since they're mounted from
 * `worker.routes.tsx` as a worker-facing entry point onto these transaction pages.
 */
export const WEEKLY_WORKER_PAYMENT_ROUTES = {
  root: "/worker-payment/weekly",
  history: "/worker-payment/weekly/history",
  session: "/worker-payment/weekly/:sessionId",
  report: "/worker-payment/weekly/:sessionId/report",
} as const;
